import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CustomizedSelectoption from "../General/CustomizedSelectoption";
import { Show_Message } from "../General/Messagedialogbox";
import { API_POST } from "../General/Utility";
import {
  Clear_POSCustomer,
  CustomerInput_Onchange,
  ShowHide_Popup,
} from "../features/Transactionstates";
import { Country_OnChange } from "../Master/MasterTransaction";

// ---------------------------------------------------------------------------
// A search-result row is POSCustomerModel (partyId, partyName, partyCode,
// aliasName, address1-4, state, country, pincode, mobile, phone, email, gst,
// remark, royaltyPoint, shippAdd, isSameAdd, status, rewardType). The lookup is
// case-insensitive and falls back to a few alternative names.
// ---------------------------------------------------------------------------
const FIELD_MAP = {
  PartyId: ["partyId", "custId", "iPartyId", "id"],
  PartyName: ["partyName", "custName", "name"],
  PartyCode: ["partyCode", "custCode", "code"],
  AliasName: ["aliasName", "alias"],
  Address1: ["address1", "add1"],
  Address2: ["address2", "add2"],
  Address3: ["address3", "add3"],
  Address4: ["address4", "add4"],
  State: ["state", "stateName", "strSName"],
  Country: ["country", "countryName", "strCountryName"],
  Pincode: ["pincode", "pinCode"],
  Mobile: ["mobile", "mobileNo", "mobileNumber"],
  Phone: ["phone", "phoneNo", "phoneNumber"],
  Email: ["email", "emailId"],
  GST: ["gst", "gstNo", "gstin"],
  Remark: ["remark", "remarks"],
  RoyaltyPoint: ["royaltyPoint", "rewardPoint", "points"],
  ShippAdd: ["shippAdd", "shippingAdd", "shippingAddress"],
  RewardType: ["rewardType"],
  Status: ["status"],
};
const pick = (row, names) => {
  if (!row) return undefined;
  const lower = {};
  Object.keys(row).forEach((k) => (lower[k.toLowerCase()] = row[k]));
  for (const n of names) {
    const v = lower[n.toLowerCase()];
    if (v !== undefined && v !== null) return v;
  }
  return undefined;
};
const rowName = (r) => pick(r, FIELD_MAP.PartyName) || "";
const rowMobile = (r) => pick(r, FIELD_MAP.Mobile) || "";

// state / country are stored as NAMES in the form; the invoice API needs ids
const STATE_ID_RE = /^i?s(tate)?_?id$/i;
const COUNTRY_ID_RE = /^i?country_?id$/i;
const findId = (list, nameKeys, name, idRe) => {
  const n = String(name || "").trim().toLowerCase();
  if (!n) return 0;
  const item = (list || []).find((x) =>
    nameKeys.some((k) => String(x[k] || "").trim().toLowerCase() === n)
  );
  if (!item) return 0;
  const key = Object.keys(item).find((k) => idRe.test(k) && Number(item[k]) > 0);
  return key ? Number(item[key]) : 0;
};

const RE_MOBILE = /^[0-9]{10}$/;
const RE_PIN = /^[0-9]{6}$/;
const RE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const RE_GST = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;

// keyboard order inside the popup
const TAB = {
  Search: 0, PartyName: 1, AliasName: 2, Mobile: 3, Phone: 4, Address1: 5,
  Address2: 6, Address3: 7, Address4: 8, Pincode: 9, State: 10, Country: 11,
  Email: 12, GST: 13, Remark: 14, IsSameAdd: 15, ShippAdd: 16, Clear: 17, Done: 18,
};
const LAST_TAB = TAB.Done;

function CustomerSearch() {
  const dispatch = useDispatch();
  const _parem = useSelector((state) => state.SelectOption.parems);
  const _AuthParem = useSelector((state) => state.Common.Admin);
  const _Customer = useSelector((state) => state.Transstates.Customer);

  const [arrCusList, setarrCusList] = useState([]);
  const [arrState, setarrState] = useState([]);
  const [arrCountry, setarrCountry] = useState([]);
  const [strSearch, setstrSearch] = useState("");
  const [isSearching, setisSearching] = useState(false);
  const [isLoaded, setisLoaded] = useState(false);

  const boxRef = useRef(null);
  const resultRefs = useRef([]);
  const searchTimer = useRef(null);
  const reqSeq = useRef(0);
  const closeRef = useRef(null);
  const clearRef = useRef(null);

  const Close_Popup = () => dispatch(ShowHide_Popup("IsPOSCustomer"));
  closeRef.current = Close_Popup;
  clearRef.current = () => dispatch(Clear_POSCustomer());

  // ✅ FIX: listener registered exactly once and removed again. (Escape used to
  // be handled twice - here AND in every input's onKeyDown - and the popup flag
  // is a toggle, so Esc closed the popup and immediately re-opened it.)
  useEffect(() => {
    Load_Customer();
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        closeRef.current();
      } else if (e.key === "F5") {
        e.preventDefault();
        clearRef.current();
      } else if (e.key === "F2") {
        e.preventDefault();
        focusTab(TAB.Search);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      clearTimeout(searchTimer.current);
    };
  }, []);

  useEffect(() => {
    if (arrState.length === 0 && arrCountry.length === 0) return;
    const sid = findId(arrState, ["strSName"], _Customer.State, STATE_ID_RE);
    const cid = findId(arrCountry, ["strCountryName"], _Customer.Country, COUNTRY_ID_RE);
    if (sid && sid !== Number(_Customer.StateId))
      dispatch(CustomerInput_Onchange({ Name: "StateId", Value: sid }));
    if (cid && cid !== Number(_Customer.CountryId))
      dispatch(CustomerInput_Onchange({ Name: "CountryId", Value: cid }));
  }, [arrState, arrCountry, _Customer.State, _Customer.Country]);

  const focusTab = (n) => {
    const root = boxRef.current;
    if (!root) return;
    // scoped to this popup (the old global $("[tabindex=N]") could grab any
    // other element on the page, and two inputs shared tabIndex 5)
    const el = root.querySelector(`[tabindex="${n}"]`);
    if (el) {
      el.focus();
      if (el.select && el.tagName === "INPUT" && el.type === "text") el.select();
    }
  };

  const Load_Customer = async () => {
    try {
      const res = await API_POST(
        `POS/Load_Customer/`,
        {
          strSearch: "",
          iBranchId: Number(_AuthParem.Branch),
          iPartyId: 0,
          strDBName: _AuthParem.DB,
        },
        dispatch
      );
      if (res) {
        setarrCusList(res.EntCustomer || []);
        setarrCountry(res.EntCountry || []);
        setarrState(res.EntState || []);
      }
    } catch (err) {
      Show_Message(dispatch, err.message || String(err), "error");
    } finally {
      setisLoaded(true);
    }
  };

  const SearchCustomer = async (strtext) => {
    const seq = ++reqSeq.current;
    try {
      setisSearching(true);
      const res = await API_POST(
        `POS/Search_Customer/`,
        {
          strSearch: strtext,
          iPartyId: 0,
          iBranchId: Number(_AuthParem.Branch),
          strDBName: _AuthParem.DB,
        },
        dispatch
      );
      if (seq !== reqSeq.current) return; // newer search already running
      if (res) setarrCusList(res.EntCustomer || []);
    } catch (err) {
      if (seq === reqSeq.current)
        Show_Message(dispatch, err.message || String(err), "error");
    } finally {
      if (seq === reqSeq.current) setisSearching(false);
    }
  };

  const Search_Change = (value) => {
    setstrSearch(value);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => SearchCustomer(value), 300);
  };

  // ✅ FIX: the popup loaded a customer list but never showed it, so an
  // existing customer could not be picked - every bill needed the customer typed
  // again. Rows are now selectable.
  const Select_Existing = (row) => {
    if (!pick(row, FIELD_MAP.PartyName) && pick(row, FIELD_MAP.PartyId) === undefined) {
      // eslint-disable-next-line no-console
      console.warn("CustomerSearch: unknown customer row shape", Object.keys(row || {}));
    }
    const set = (Name, Value) =>
      dispatch(CustomerInput_Onchange({ Name, Value }));
    const get = (k, d = "") => {
      const v = pick(row, FIELD_MAP[k]);
      return v === undefined ? d : v;
    };
    set("PartyId", Number(get("PartyId", 0)) || 0);
    ["PartyName", "PartyCode", "AliasName", "Address1", "Address2", "Address3",
      "Address4", "Pincode", "Mobile", "Phone", "Email", "GST", "Remark",
      "Status"].forEach((k) => set(k, String(get(k, ""))));
    // RewardType is a shop setting, not a customer value: never overwrite it
    // with an empty string coming from the customer row.
    const rt = String(get("RewardType", ""));
    if (rt !== "") set("RewardType", rt);
    const st = get("State", "");
    const co = get("Country", "");
    if (st !== "") set("State", String(st));
    if (co !== "") set("Country", String(co));
    set("StateId", findId(arrState, ["strSName"], st, STATE_ID_RE));
    set("CountryId", findId(arrCountry, ["strCountryName"], co, COUNTRY_ID_RE));
    set("RoyaltyPoint", Number(get("RoyaltyPoint", 0)) || 0);
    const ship = String(get("ShippAdd", ""));
    set("ShippAdd", ship);
    const sameFlag = pick(row, ["isSameAdd"]);
    set("IsSameAdd", ship === "" || Number(sameFlag) === 1 || sameFlag === true);
    Close_Popup();
  };

  const Use_WalkIn = () => {
    dispatch(Clear_POSCustomer());
    Close_Popup();
  };

  const Input_ValueChange = async (e) => {
    await dispatch(
      CustomerInput_Onchange({
        Name: e.target.name,
        Value: e.target.value,
      })
    );
  };

  // validation + close. Nothing typed = walk-in customer.
  const Done_Click = () => {
    const c = _Customer;
    const name = String(c.PartyName || "").trim();
    const mobile = String(c.Mobile || "").replace(/[\s-]/g, "");
    const gst = String(c.GST || "").trim().toUpperCase();
    const email = String(c.Email || "").trim();
    const pin = String(c.Pincode || "").trim();
    const anyDetail = [c.AliasName, c.Mobile, c.Phone, c.Address1, c.Address2,
      c.Address3, c.Address4, c.Pincode, c.Email, c.GST, c.Remark].some(
      (v) => String(v || "").trim() !== ""
    );
    let err = "";
    if (name === "" && anyDetail) err = "Customer name is required";
    else if (mobile !== "" && !RE_MOBILE.test(mobile))
      err = "Enter a valid 10 digit mobile number";
    else if (pin !== "" && !RE_PIN.test(pin)) err = "Enter a valid 6 digit pincode";
    else if (email !== "" && !RE_EMAIL.test(email)) err = "Enter a valid e-mail address";
    else if (gst !== "" && !RE_GST.test(gst)) err = "Enter a valid 15 character GSTIN";
    if (err) {
      Show_Message(dispatch, err, "info");
      return;
    }
    if (gst !== c.GST) dispatch(CustomerInput_Onchange({ Name: "GST", Value: gst }));
    if (mobile !== c.Mobile)
      dispatch(CustomerInput_Onchange({ Name: "Mobile", Value: mobile }));
    Close_Popup();
  };

  // Arrow / Enter navigation between fields (Esc / F5 / F2 are handled once by
  // the document listener above).
  const Key_Down = (e) => {
    const tag = e.target.tagName;
    const idx = Number(e.target.getAttribute("tabindex"));
    if (tag === "TEXTAREA" && (e.key === "Enter" || e.key === "ArrowUp" || e.key === "ArrowDown")) {
      if (e.key !== "Enter" || e.shiftKey) return; // keep new lines / caret keys
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      focusTab(idx >= LAST_TAB ? TAB.PartyName : idx + 1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      focusTab(idx <= TAB.PartyName ? LAST_TAB : idx - 1);
    } else if (e.key === "Enter") {
      if (tag === "BUTTON") return; // let the button click itself
      e.preventDefault();
      focusTab(idx >= LAST_TAB ? TAB.PartyName : idx + 1);
    }
  };

  const Search_KeyDown = (e) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (arrCusList.length > 0 && resultRefs.current[0]) resultRefs.current[0].focus();
      else focusTab(TAB.PartyName);
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (arrCusList.length > 0) Select_Existing(arrCusList[0]);
      else focusTab(TAB.PartyName);
    }
  };

  const shown = arrCusList.slice(0, 8);

  return (
    <div className="popup-container">
      <div className="popup-content" ref={boxRef}>
        <div className="popup-header">
          <h5>Select Customer</h5>
          <div className="icon-popup-btn-grp">
            <span
              className="icon-btn"
              role="button"
              title="Close (Esc)"
              onClick={(e) => Close_Popup()}
            >
              <i className="fa-regular fa-rectangle-xmark"></i>
            </span>
          </div>
        </div>
        <div className="popup-body pb-4">
          {/* ---------- find an existing customer ---------- */}
          <div className="row">
            <div className="col-sm-12 mt-2">
              <label>Find Customer (F2)</label>
              <input
                type="text"
                className="form-control"
                tabIndex={TAB.Search}
                value={strSearch}
                autoFocus
                spellCheck={false}
                autoComplete="off"
                placeholder="Search by name or mobile number"
                onChange={(e) => Search_Change(e.target.value)}
                onKeyDown={Search_KeyDown}
              />
              <div style={{ marginTop: "6px", maxHeight: "190px", overflowY: "auto" }}>
                <button
                  type="button"
                  className="btn-fabgreen"
                  style={{ width: "100%", textAlign: "left", marginBottom: "4px" }}
                  onClick={Use_WalkIn}
                >
                  <i className="fa-solid fa-person-walking"></i>&nbsp; Walk-in Customer
                </button>
                {isSearching && (
                  <div style={{ padding: "6px", color: "#888" }}>Searching...</div>
                )}
                {isLoaded && !isSearching && shown.length === 0 && (
                  <div style={{ padding: "6px", color: "#888" }}>
                    No customer found. Fill the details below to add a new one.
                  </div>
                )}
                {shown.map((row, i) => (
                  <button
                    type="button"
                    key={i}
                    ref={(el) => (resultRefs.current[i] = el)}
                    className="btn-fabgreen"
                    style={{
                      width: "100%",
                      textAlign: "left",
                      marginBottom: "4px",
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                    onClick={() => Select_Existing(row)}
                    onKeyDown={(e) => {
                      if (e.key === "ArrowDown") {
                        e.preventDefault();
                        const n = resultRefs.current[i + 1];
                        if (n) n.focus();
                      } else if (e.key === "ArrowUp") {
                        e.preventDefault();
                        if (i === 0) focusTab(TAB.Search);
                        else resultRefs.current[i - 1].focus();
                      }
                    }}
                  >
                    <span>{rowName(row) || "(no name)"}</span>
                    <span style={{ opacity: 0.7 }}>{rowMobile(row)}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <hr style={{ margin: "10px 0" }} />
          <h6 style={{ marginBottom: 0 }}>New / Edit Customer Details</h6>
          <div className="row">
            <div className="col-sm-12 col-md-6 mt-2">
              <label>Customer Name</label>
              <input
                type="text"
                className="form-control"
                name="PartyName"
                spellCheck={false}
                autoComplete="off"
                tabIndex={TAB.PartyName}
                value={_Customer.PartyName}
                onChange={(e) => Input_ValueChange(e)}
                placeholder="Customer Name"
                onKeyDown={Key_Down}
              />
            </div>
            <div className="col-sm-12 col-md-3 mt-2">
              <label>Alias Name</label>
              <input
                type="text"
                name="AliasName"
                className="form-control"
                spellCheck={false}
                autoComplete="off"
                tabIndex={TAB.AliasName}
                value={_Customer.AliasName}
                onChange={(e) => Input_ValueChange(e)}
                placeholder="Alias Name"
                onKeyDown={Key_Down}
              />
            </div>
            <div className="col-sm-6 col-md-3 mt-2">
              <label>Mobile Number</label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={10}
                className="form-control"
                spellCheck={false}
                autoComplete="off"
                name="Mobile"
                value={_Customer.Mobile}
                tabIndex={TAB.Mobile}
                onChange={(e) =>
                  Input_ValueChange({
                    target: { name: "Mobile", value: e.target.value.replace(/[^0-9]/g, "") },
                  })
                }
                placeholder="10 digit mobile"
                onKeyDown={Key_Down}
              />
            </div>
            <div className="col-sm-6 col-md-3 mt-2">
              <label>Phone No</label>
              <input
                type="text"
                className="form-control"
                spellCheck={false}
                autoComplete="off"
                tabIndex={TAB.Phone}
                name="Phone"
                value={_Customer.Phone}
                onChange={(e) => Input_ValueChange(e)}
                placeholder="Phone Number"
                onKeyDown={Key_Down}
              />
            </div>
            <div className="col-sm-6 col-md-3 mt-2">
              <label>Door / Office No</label>
              <input
                type="text"
                tabIndex={TAB.Address1}
                className="form-control"
                spellCheck={false}
                autoComplete="off"
                name="Address1"
                value={_Customer.Address1}
                onChange={(e) => Input_ValueChange(e)}
                placeholder="Flat / Door No"
                onKeyDown={Key_Down}
              />
            </div>
            <div className="col-sm-6 col-md-3 mt-2">
              <label>Street / Area</label>
              <input
                type="text"
                tabIndex={TAB.Address2}
                className="form-control"
                spellCheck={false}
                autoComplete="off"
                value={_Customer.Address2}
                name="Address2"
                onChange={(e) => Input_ValueChange(e)}
                placeholder="Street"
                onKeyDown={Key_Down}
              />
            </div>
            <div className="col-sm-6 col-md-3 mt-2">
              <label>Village / Town</label>
              <input
                type="text"
                tabIndex={TAB.Address3}
                className="form-control"
                spellCheck={false}
                autoComplete="off"
                value={_Customer.Address3}
                name="Address3"
                onChange={(e) => Input_ValueChange(e)}
                placeholder="Area"
                onKeyDown={Key_Down}
              />
            </div>
            <div className="col-sm-6 col-md-3 mt-2">
              <label>City</label>
              <input
                type="text"
                tabIndex={TAB.Address4}
                spellCheck={false}
                autoComplete="off"
                className="form-control"
                value={_Customer.Address4}
                name="Address4"
                onChange={(e) => Input_ValueChange(e)}
                placeholder="City"
                onKeyDown={Key_Down}
              />
            </div>
            <div className="col-sm-6 col-md-3 mt-2">
              <label>Pincode</label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                className="form-control"
                tabIndex={TAB.Pincode}
                spellCheck={false}
                autoComplete="off"
                name="Pincode"
                value={_Customer.Pincode}
                onChange={(e) =>
                  Input_ValueChange({
                    target: { name: "Pincode", value: e.target.value.replace(/[^0-9]/g, "") },
                  })
                }
                placeholder="Pincode"
                onKeyDown={Key_Down}
              />
            </div>
            <div className="col-sm-6 col-md-3 mt-2">
              <label>State</label>
              <CustomizedSelectoption
                optionparem="isoptionShow1"
                showoption={_parem.isoptionShow1}
                placeholder="Select State"
                tabIndex={TAB.State}
                onKeyDown={Key_Down}
                select_value={(val) => {
                  Input_ValueChange({
                    target: { name: "State", value: val.strSName },
                  });
                  const key = Object.keys(val).find((k) => STATE_ID_RE.test(k));
                  Input_ValueChange({
                    target: { name: "StateId", value: key ? Number(val[key]) : 0 },
                  });
                }}
                btnname="Customer"
                btnshow={false}
                show_popup={""}
                valueonly={false}
                defaultval={_Customer.State}
                displayName="strSName"
                disvalue="strSName"
                arrydata={arrState}
                disabled={false}
                EmptVal="State"
              />
            </div>
            <div className="col-sm-6 col-md-3 mt-2">
              <label>Country</label>
              <CustomizedSelectoption
                optionparem="isoptionShow2"
                showoption={_parem.isoptionShow2}
                placeholder="Select Country"
                tabIndex={TAB.Country}
                onKeyDown={Key_Down}
                select_value={async (val) => {
                  Input_ValueChange({
                    target: { name: "Country", value: val.strCountryName },
                  });
                  Input_ValueChange({
                    target: { name: "CountryId", value: Number(val.iCountryId) || 0 },
                  });
                  const states = await Country_OnChange(val.iCountryId, _AuthParem.DB, dispatch);
                  setarrState(states || []);
                }}
                btnname="Country"
                btnshow={false}
                show_popup={""}
                valueonly={false}
                defaultval={_Customer.Country}
                displayName="strCountryName"
                disvalue="iCountryId"
                arrydata={arrCountry}
                disabled={false}
                EmptVal="Country"
              />
            </div>
            <div className="col-sm-6 col-md-3 mt-2">
              <label>E-Mail</label>
              <input
                type="text"
                className="form-control"
                name="Email"
                value={_Customer.Email}
                spellCheck={false}
                autoComplete="off"
                tabIndex={TAB.Email}
                onChange={(e) => Input_ValueChange(e)}
                placeholder="name@example.com"
                onKeyDown={Key_Down}
              />
            </div>
            <div className="col-sm-6 col-md-3 mt-2">
              <label>GSTIN</label>
              <input
                type="text"
                className="form-control"
                name="GST"
                maxLength={15}
                spellCheck={false}
                autoComplete="off"
                tabIndex={TAB.GST}
                value={_Customer.GST}
                onChange={(e) =>
                  Input_ValueChange({
                    target: { name: "GST", value: e.target.value.toUpperCase() },
                  })
                }
                placeholder="15 character GSTIN"
                onKeyDown={Key_Down}
              />
            </div>
            <div className="col-sm-6 col-md-3 mt-2">
              <label>Remark</label>
              <input
                type="text"
                className="form-control"
                name="Remark"
                spellCheck={false}
                autoComplete="off"
                tabIndex={TAB.Remark}
                value={_Customer.Remark}
                onChange={(e) => Input_ValueChange(e)}
                placeholder="Remark"
                onKeyDown={Key_Down}
              />
            </div>
            <div className="col-sm-12 col-md-6 mt-2">
              <div className="flex-wrap mb-1">
                <input
                  type="checkbox"
                  id="chkSameAdd"
                  name="IsSameAdd"
                  tabIndex={TAB.IsSameAdd}
                  checked={!!_Customer.IsSameAdd}
                  onChange={(e) =>
                    Input_ValueChange({
                      target: { name: "IsSameAdd", value: !_Customer.IsSameAdd },
                    })
                  }
                  onKeyDown={Key_Down}
                />
                <label htmlFor="chkSameAdd" style={{ cursor: "pointer", marginLeft: "6px" }}>
                  Delivery address same as billing address
                </label>
              </div>
              <textarea
                disabled={!!_Customer.IsSameAdd}
                className="form-control"
                name="ShippAdd"
                value={_Customer.ShippAdd}
                style={{ minHeight: "80px" }}
                tabIndex={TAB.ShippAdd}
                spellCheck={false}
                autoComplete="off"
                onChange={(e) => Input_ValueChange(e)}
                placeholder="Delivery address"
                onKeyDown={Key_Down}
              />
            </div>
          </div>
        </div>
        <div className="popup-footer">
          <div className="popup-btn-group">
            <button
              onClick={(e) => dispatch(Clear_POSCustomer())}
              tabIndex={TAB.Clear}
              className="btn-fabgreen"
              onKeyDown={Key_Down}
            >
              Clear (F5)
            </button>
            <button
              onClick={Done_Click}
              tabIndex={TAB.Done}
              className="btn-fabgreen"
              onKeyDown={Key_Down}
            >
              Use Customer&nbsp;<i className="fa-solid fa-check"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
export default CustomerSearch;